import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  RedemptionGroupBy,
  RedemptionProductView,
  REDEMPTION_AGG_SORT,
  REDEMPTION_JOURNEY_SORT,
  sortWhitelistForView,
} from "../../../../../../models/Dashboard/Redemption/RedemptionFilters";
import { RedemptionStat } from "../../../../../../models/Dashboard/Redemption/RedemptionDrilldown";
import { RedemptionHow } from "../../../../../../models/Dashboard/Redemption/RedemptionSummary";
import { RedemptionDashboardStore } from "../../../../stores/dashboards/RedemptionDashboardStore";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
} from "../../../../../../styles/modernListingPageSx";
import RedemptionDataTable from "./RedemptionDataTable";
import RedemptionHint from "./RedemptionHint";
import { formatRedemptionStat, redemptionRangeSlug } from "./redemptionFormat";
import { trackRedemptionAction } from "../../../../../../analytics/redemptionTracking";

const DRAWER_Z_OFFSET = 2000;

const PRODUCT_VIEWS: Array<{ id: RedemptionProductView; label: string }> = [
  { id: "lines", label: "Redemption lines" },
  { id: "pack", label: "By pack" },
  { id: "variant", label: "By variant" },
  { id: "city", label: "By city" },
];

const MATRIX_VIEWS: Array<{ id: RedemptionGroupBy; label: string }> = [
  { id: "product", label: "Product · variant" },
  { id: "city", label: "By city" },
];

const SOURCE_LABEL: Record<string, string> = {
  individual: "Individual",
  onelink: "Onelink",
  all: "All",
};

interface RedemptionDrawerProps {
  store: RedemptionDashboardStore;
}

const visibleRange = (
  page: number,
  pageSize: number,
  total: number,
  rowCount: number,
): { from: number; to: number } => {
  try {
    if (total <= 0 || rowCount <= 0) {
      return { from: 0, to: 0 };
    }
    const from = (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + 1;
    return { from, to: from + rowCount - 1 };
  } catch (error) {
    console.error(error);
    return { from: 0, to: 0 };
  }
};

const RedemptionDrawer: React.FC<RedemptionDrawerProps> = ({ store }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const [formulaOpen, setFormulaOpen] = useState(true);
  const isJourney = store.drawerKind === "journey";
  const isProduct = store.drawerKind === "product";
  const journeyRaw = store.journeyResult?.data;
  const productRaw = store.productResult?.data;
  const matrix = store.matrixResult?.data;
  const sourceLabel = SOURCE_LABEL[store.appliedFilters.source] || "Individual";

  const toggleFormulaOpen = () => {
    setFormulaOpen((current) => {
      const next = !current;
      const journeyStage =
        store.drawerJourneyStage ||
        (store.journeyMetricId?.startsWith("journey:")
          ? store.journeyMetricId.replace(/^journey:/, "")
          : undefined);
      trackRedemptionAction("formula_toggle", store.appliedFilters, {
        expanded: next,
        drawerKind: store.drawerKind,
        journeyStage,
        cardTitle: store.drawerCardTitle || undefined,
        productId: store.productId ?? undefined,
        context: {
          drawer_kind: store.drawerKind,
          expanded: next,
          ...(store.drawerCardTitle
            ? { card_title: store.drawerCardTitle }
            : {}),
          ...(journeyStage ? { journey_stage: journeyStage } : {}),
        },
      });
      return next;
    });
  };

  const journeyMatches =
    isJourney &&
    Boolean(journeyRaw) &&
    (store.journeyResult?.meta?.metricId === store.journeyMetricId ||
      (journeyRaw?.stageId != null &&
        store.journeyMetricId === `journey:${journeyRaw.stageId}`));

  const productMatches =
    isProduct &&
    Boolean(productRaw) &&
    productRaw?.product_id === store.productId;

  const journey = journeyMatches ? journeyRaw : null;
  const product = productMatches ? productRaw : null;
  const hasData = Boolean(
    (isJourney && journey) ||
      (isProduct && product) ||
      (!isJourney && !isProduct && matrix),
  );

  const summaryStage = store.summaryResult?.data.journey?.stages?.find(
    (stage) => stage.metricId === store.journeyMetricId,
  );

  useEffect(() => {
    try {
      if (store.drawerOpen) {
        setFormulaOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [store.drawerOpen, store.drawerKind, store.journeyMetricId, store.productId]);

  const kicker = isJourney
    ? `Coupon journey · ${sourceLabel}`
    : isProduct
      ? `Product · ${sourceLabel}`
      : "Drill-down · scoped to filter";
  const title = isJourney
    ? journey?.label || summaryStage?.label || "Coupons"
    : isProduct
      ? product?.product_name || "Product"
      : store.groupBy === "city"
        ? "City · product · variant · count"
        : "Product · variant · count";
  const desc = isJourney
    ? "Coupons in this stage for the active filter."
    : isProduct
      ? "Redeemed units, packs, variants and cities for this product."
      : "Every product and its variant counts for the active order / customer.";

  const stats: RedemptionStat[] = isJourney
    ? journey?.stats ?? []
    : isProduct
      ? product?.stats ?? []
      : matrix
        ? [
            { label: "Units", value: matrix.totals.units },
            { label: "Products", value: matrix.totals.products },
            { label: "Variants", value: matrix.totals.variants },
            { label: "Coupons", value: matrix.totals.coupons },
          ]
        : [];

  const table = isJourney
    ? journey?.table ?? null
    : isProduct
      ? product?.table ?? null
      : matrix?.table ?? null;
  const sortable = isJourney
    ? REDEMPTION_JOURNEY_SORT
    : isProduct
      ? sortWhitelistForView(store.productView)
      : REDEMPTION_AGG_SORT;
  const formula = isJourney
    ? journey?.formula
    : isProduct
      ? product?.formula
      : "";
  const how: RedemptionHow | null = isJourney
    ? journey?.how ?? null
    : isProduct
      ? product?.how ?? null
      : matrix?.how ?? null;
  const hasHowContent = Boolean(
    how?.info?.trim() || (how?.fields && how.fields.length > 0) || formula,
  );
  const rows = table?.rows ?? [];
  const page = table?.page ?? store.page;
  const pageSize = table?.page_size ?? store.pageSize;
  const total = table?.total ?? 0;
  const range = visibleRange(page, pageSize, total, rows.length);
  const drawerBodyKey = isJourney
    ? store.journeyMetricId || "journey"
    : isProduct
      ? `product-${store.productId}-${store.productView}`
      : `matrix-${store.groupBy}`;

  const handleClose = () => {
    try {
      store.closeDrawer();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRefresh = () => {
    try {
      void store.refreshData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleExport = () => {
    try {
      const dateRange = redemptionRangeSlug(
        store.appliedFilters.start_date,
        store.appliedFilters.end_date,
      );
      const fallback = isJourney
        ? `ops-redemptions-${(store.journeyMetricId || "journey").replace(":", "_")}-${dateRange}.csv`
        : isProduct
          ? `ops-redemptions-product_${store.productId}_${store.productView}-${dateRange}.csv`
          : `ops-redemptions-variant-matrix_${store.groupBy}-${dateRange}.csv`;
      void store.exportCsv(fallback);
    } catch (error) {
      console.error(error);
    }
  };

  const handleView = (view: RedemptionProductView) => {
    try {
      void store.changeProductView(view);
    } catch (error) {
      console.error(error);
    }
  };

  const handleGroup = (groupBy: RedemptionGroupBy) => {
    try {
      void store.changeGroupBy(groupBy);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={store.drawerOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 880, md: 960 },
          maxWidth: "100vw",
          height: "100%",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="flex-start" minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: customColors.lightBlue,
              color: customColors.darkBlue,
            }}
          >
            <InsightsOutlinedIcon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <CustomTypography
              variant="caption"
              sx={{
                color: colors.subtitle,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {kicker}
            </CustomTypography>
            <CustomTypography
              variant="heading5"
              sx={{ color: colors.title, wordBreak: "break-word" }}
            >
              {title}
            </CustomTypography>
            <Box sx={{ mt: 0.25 }}>
              <RedemptionHint how={how} />
            </Box>
            {desc ? (
              <CustomTypography
                variant="body4"
                sx={{
                  mt: 0.5,
                  color: colors.subtitle,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                {desc}
              </CustomTypography>
            ) : null}
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
          <IconButton
            onClick={handleRefresh}
            size="small"
            aria-label="Refresh"
            title="Refresh with applied filters"
            disabled={store.drawerLoading || store.summaryLoading}
          >
            {store.drawerLoading ? (
              <CircularProgress size={18} />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
          <IconButton onClick={handleClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          ...modernDrawerBodySx(),
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {store.drawerError ? (
          <Alert
            severity="error"
            sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
          >
            {store.drawerError}
          </Alert>
        ) : null}
        {store.exportError ? (
          <Alert
            severity="error"
            sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
          >
            {store.exportError}
          </Alert>
        ) : null}

        {store.drawerLoading && !hasData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {hasData ? (
          <Box
            key={drawerBodyKey}
            sx={{ display: "flex", flexDirection: "column", gap: "inherit" }}
          >
            {stats.length > 0 ? (
              <Grid2 container spacing={1.5}>
                {stats.map((stat, index) => (
                  <Grid2 key={`${stat.label}-${index}`} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        height: "100%",
                        borderRadius: 2.5,
                        bgcolor: customColors.white,
                        border: `1px solid ${customColors.softCardBorder}`,
                        borderLeft: `4px solid ${customColors.darkBlue}`,
                        boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                      }}
                    >
                      <CustomTypography
                        variant="body4"
                        sx={{
                          color: customColors.mutedSlateText,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                        }}
                      >
                        {stat.label}
                      </CustomTypography>
                      <CustomTypography
                        variant="heading5"
                        sx={{
                          mt: 0.5,
                          color: customColors.navyText,
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          wordBreak: "break-word",
                        }}
                      >
                        {formatRedemptionStat(stat.value)}
                      </CustomTypography>
                    </Paper>
                  </Grid2>
                ))}
              </Grid2>
            ) : null}

            {hasHowContent ? (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${colors.border}`,
                  overflow: "hidden",
                  boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                }}
              >
                <Box
                  onClick={toggleFormulaOpen}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    bgcolor: customColors.white,
                    "&:hover": { bgcolor: customColors.hoverGray },
                  }}
                >
                  <CustomTypography variant="heading7" sx={{ color: colors.title }}>
                    How this is calculated
                  </CustomTypography>
                  <IconButton
                    size="small"
                    aria-label={formulaOpen ? "Collapse formula" : "Expand formula"}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleFormulaOpen();
                    }}
                  >
                    {formulaOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={formulaOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {how?.info ? (
                      <CustomTypography
                        variant="body4"
                        sx={{
                          color: customColors.navyText,
                          whiteSpace: "pre-wrap",
                          fontSize: "0.8rem",
                        }}
                      >
                        {how.info}
                      </CustomTypography>
                    ) : null}
                    {how?.fields && how.fields.length > 0 ? (
                      <CustomTypography
                        variant="body4"
                        sx={{
                          color: customColors.mutedSlateText,
                          fontSize: "0.75rem",
                        }}
                      >
                        Fields: {how.fields.join(", ")}
                      </CustomTypography>
                    ) : null}
                    {formula ? (
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          bgcolor: customColors.veryLightGray,
                          border: `1px solid ${customColors.lightGray}`,
                          overflowX: "auto",
                        }}
                      >
                        <CustomTypography
                          variant="body4"
                          sx={{
                            color: customColors.navyText,
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                          }}
                        >
                          {formula}
                        </CustomTypography>
                      </Box>
                    ) : null}
                  </Box>
                </Collapse>
              </Paper>
            ) : null}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <CustomTypography variant="heading7" sx={{ color: colors.title }}>
                  Records
                </CustomTypography>
                {isProduct
                  ? PRODUCT_VIEWS.map((view) => (
                      <AppButton
                        key={view.id}
                        variant={
                          store.productView === view.id
                            ? "primary"
                            : "transparentPrimary"
                        }
                        label={view.label}
                        onClick={() => handleView(view.id)}
                        disabled={store.drawerLoading}
                        customStyles={{ minHeight: 32, px: 1.5 }}
                        fontSize="0.75rem"
                      />
                    ))
                  : null}
                {!isJourney && !isProduct
                  ? MATRIX_VIEWS.map((view) => (
                      <AppButton
                        key={view.id}
                        variant={
                          store.groupBy === view.id ? "primary" : "transparentPrimary"
                        }
                        label={view.label}
                        onClick={() => handleGroup(view.id)}
                        disabled={store.drawerLoading}
                        customStyles={{ minHeight: 32, px: 1.5 }}
                        fontSize="0.75rem"
                      />
                    ))
                  : null}
              </Box>
              <AppButton
                variant="primary"
                label={store.exportLoading ? "Exporting..." : "Export CSV"}
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={store.exportLoading || store.drawerLoading}
                customStyles={{ minHeight: 32 }}
                fontSize="0.75rem"
              />
            </Box>

            <CustomTypography
              variant="body4"
              sx={{
                color: customColors.mutedSlateText,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {total <= 0
                ? "Showing 0 of 0"
                : `Showing ${range.from}–${range.to} of ${total}`}
            </CustomTypography>

            {rows.length === 0 && !store.drawerLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10rem"
                textAlign="center"
                border="1px dashed"
                borderColor="divider"
                borderRadius={2}
              >
                <CustomTypography
                  variant="heading6"
                  fontWeight={500}
                  color={customColors.greyTextColor}
                >
                  No records for this view.
                </CustomTypography>
              </Box>
            ) : rows.length > 0 ? (
              <Box sx={{ position: "relative" }}>
                {store.drawerLoading ? (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${customColors.white}99`,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : null}
                <RedemptionDataTable
                  maxHeight={window.innerHeight - 350}
                  columns={table?.columns ?? []}
                  rows={rows}
                  pageNo={page}
                  limit={pageSize}
                  totalCount={total}
                  sortableColumns={sortable}
                  sortBy={table?.sort?.column}
                  sortDir={table?.sort?.dir}
                  onSort={(column) => {
                    try {
                      void store.changeSort(column);
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                  onPageChange={(_event, nextPage) => {
                    try {
                      void store.changePage(nextPage);
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                  onPageSizeChange={(pageSizeNext) => {
                    try {
                      void store.changePageSize(pageSizeNext);
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                />
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default observer(RedemptionDrawer);
